import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import FileTree from '../components/FileTree';
import CodeEditor from '../components/CodeEditor';
import Terminal from '../components/Terminal';
import AIPanel from '../components/AIPanel';
import ServicePanel from '../components/ServicePanel';
import CrossReviewPanel from '../components/CrossReviewPanel';
import AcceptancePanel from '../components/AcceptancePanel';
import FiveDimensionPanel from '../components/FiveDimensionPanel';
import DeployPanel from '../components/DeployPanel';
import axios from 'axios';

export default function Workspace() {
  const { name } = useParams();
  const [projectPath, setProjectPath] = useState('');
  const [currentFile, setCurrentFile] = useState(null);
  const [fileContent, setFileContent] = useState('');
  const [activePanel, setActivePanel] = useState('');
  const [history, setHistory] = useState([]);

  useEffect(() => {
    axios
      .get(`/api/projects/path/${encodeURIComponent(name)}`)
      .then(res => setProjectPath(res.data.path))
      .catch(err => {
        console.error('加载项目路径失败:', err);
        setProjectPath('');
      });

    axios
      .get(`/api/projects/${encodeURIComponent(name)}/history`)
      .then(res => setHistory(Array.isArray(res.data) ? res.data : []))
      .catch(err => {
        console.warn('加载项目历史失败:', err.message);
        setHistory([]);
      });
  }, [name]);

  const normalizeFileContent = (data) => {
    if (typeof data === 'string') return data;

    const content = data?.content ?? data?.data ?? data?.body ?? '';

    if (typeof content === 'string') return content;

    try {
      return JSON.stringify(content, null, 2);
    } catch {
      return String(content ?? '');
    }
  };

  const openFile = async (filePath) => {
    try {
      const res = await axios.get(
        `/api/files/read?filePath=${encodeURIComponent(filePath)}`,
        {
          responseType: 'text',
          transformResponse: [(data) => data]
        }
      );

      setCurrentFile(filePath);
      setFileContent(normalizeFileContent(res.data));
    } catch (err) {
      console.error('打开文件失败:', err);
      alert('打开文件失败');
      setCurrentFile(filePath);
      setFileContent('');
    }
  };

  const saveFile = async () => {
    if (!currentFile) {
      alert('请先选择文件');
      return;
    }

    try {
      await axios.post('/api/files/write', {
        filePath: currentFile,
        content: String(fileContent ?? '')
      });

      alert('保存成功');
    } catch (err) {
      console.error('保存文件失败:', err);
      alert('保存失败');
    }
  };

  const togglePanel = (panel) => {
    setActivePanel(activePanel === panel ? '' : panel);
  };

  const handleEditorChange = (value) => {
    setFileContent(String(value ?? ''));
  };

  const renderPanel = () => {
    if (activePanel === 'ai') {
      return <AIPanel currentCode={String(fileContent ?? '')} />;
    }

    if (activePanel === 'review') {
      return (
        <CrossReviewPanel
          projectName={name}
          currentFile={currentFile}
          currentCode={String(fileContent ?? '')}
        />
      );
    }

    if (activePanel === 'acceptance') {
      return <AcceptancePanel projectName={name} />;
    }

    if (activePanel === 'five') {
      return <FiveDimensionPanel projectName={name} />;
    }

    if (activePanel === 'service') {
      return <ServicePanel projectName={name} />;
    }

    if (activePanel === 'deploy') {
      return <DeployPanel projectName={name} />;
    }

    return null;
  };

  const buttonClass = (panel, color) => {
    const active = activePanel === panel;
    return `px-3 py-1 rounded ${active ? 'ring-2 ring-white ' : ''}${color}`;
  };

  return (
    <div className="h-screen flex flex-col bg-gray-900 text-white">
      <div className="bg-gray-800 p-3 flex items-center gap-3 border-b border-gray-700">
        <span className="font-bold text-lg mr-2">📦 {name}</span>

        <button onClick={() => togglePanel('ai')} className={buttonClass('ai', 'bg-purple-600')}>
          AI助手
        </button>

        <button onClick={() => togglePanel('review')} className={buttonClass('review', 'bg-orange-600')}>
          双模型评审
        </button>

        <button onClick={() => togglePanel('acceptance')} className={buttonClass('acceptance', 'bg-blue-600')}>
          验收检测
        </button>

        <button onClick={() => togglePanel('five')} className={buttonClass('five', 'bg-cyan-600')}>
          五维解构
        </button>

        <button onClick={() => togglePanel('service')} className={buttonClass('service', 'bg-green-600')}>
          服务控制
        </button>

        <button onClick={() => togglePanel('deploy')} className={buttonClass('deploy', 'bg-indigo-600')}>
          部署打包
        </button>

        <button onClick={saveFile} className="px-3 py-1 bg-gray-600 rounded ml-auto">
          保存文件
        </button>
      </div>

      <div className="bg-gray-950 border-b border-gray-800 px-3 py-2 text-xs text-gray-300 flex gap-4">
        <span>宪法：易传五维</span>
        <span>流程：检测 → 解构 → 双评审 → 补丁 → 回归 → 交付</span>
        <span>验收：项目自带 acceptance 文件</span>
      </div>

      <div className="flex flex-1 overflow-hidden">
        <div className="w-64 bg-gray-850 border-r border-gray-700 overflow-auto">
          {projectPath && <FileTree rootPath={projectPath} onSelect={openFile} />}
        </div>

        <div className="flex-1 flex flex-col">
          <div className="bg-gray-800 border-b border-gray-700 px-3 py-2 text-xs text-gray-300 truncate">
            当前文件：{currentFile || '未选择文件'}
          </div>

          <div className="flex-1">
            <CodeEditor
              filePath={currentFile}
              content={String(fileContent ?? '')}
              onChange={handleEditorChange}
            />
          </div>

          <div className="h-48 border-t border-gray-700">
            <Terminal projectPath={projectPath} />
          </div>
        </div>

        {activePanel && (
          <div className="w-96 bg-gray-800 border-l border-gray-700 overflow-auto">
            {renderPanel()}
          </div>
        )}
      </div>

      <div className="bg-gray-800 p-3 border-t border-gray-700">
        <details>
          <summary className="cursor-pointer text-sm">📜 项目修改历史 / 审计记录</summary>
          <div className="mt-2 max-h-40 overflow-auto text-xs">
            {history.length === 0 && (
              <div className="text-gray-400">暂无历史记录。后续将接入 .devstudio/runs 审计链。</div>
            )}

            {history.map((h, i) => (
              <div key={i} className="mb-1">
                {h.time} - {h.file} : {h.action}
              </div>
            ))}
          </div>
        </details>
      </div>
    </div>
  );
}
